/*eslint-disable */
import React, { FC, memo, useMemo, useEffect, useCallback } from "react";

import { areEqualShallow } from "Utils/areEqual";
import TrailsList from "Components/TrailsList";
import { setPathStops, setStopState, StopState } from "Containers/Stops";
import { setCurrentStop, stopChangeTrigger } from "Containers/Stops/currentStop.Actions";
import { navigateToTrail } from "Containers/Trails/TrailsActions";
import { useDispatch, useSelector } from "react-redux";
import { isMobile } from "Utils/browserProperties";
import { setShowSearchOptions } from "Containers/CampusMap";

interface Props {
  setTrails: (val: any) => void;
  setShowTrails: (showTrails: boolean) => void;
  showTrails: boolean;
  selectedTrails: number;
  setSelectedTrails: (trails: number) => void;
  showDefaultMobileSearchOptions: boolean;
  setShowDefaultMobileSearchOptions: (status: boolean) => void;
  selectedTrail: string;
  setSelectedTrail: (trail: string) => void;
}

// Add the points to an object, for quick access, when rendering trails.
// Each entry, will be associated with a trail, using the index number.  This will also be associated with each trail button created, using the same index
const generatePathStops = (trails: any[]) => {
  const pathStops = {};
  if (trails && Array.isArray(trails)) {
    trails.forEach(({ Stops }, index: number) => {
      if (Stops) {
        pathStops[index] = Stops;
      }
    });
  }
  return pathStops;
};

const TrailsListContainer: FC<Props> = memo(
  ({
    setTrails,
    setShowTrails,
    showTrails,
    selectedTrails,
    setSelectedTrails,
    showDefaultMobileSearchOptions,
    setShowDefaultMobileSearchOptions,
    selectedTrail,
    setSelectedTrail,
  }: Props) => {
    const dispatch = useDispatch();
    // We need to generate the pathStops for each Trail, and pass the array into the TrailListComponent

    const { suggestedTrails, currentTrailIndex } = useSelector(
      (selectors: any) => selectors.trails
    );
    const view = useSelector((store: any) => store.searchOptionsView.view);

    // We just want an object containing an array of points.
    // let's only do it once, until suggestions change.
    const pathStops = useMemo(() => generatePathStops(suggestedTrails), [
      suggestedTrails,
    ]);

    useEffect(() => {
      // Only dispatch if the value is not -1.  Get the points array, associated with the index of the button that was clicked.
      if (selectedTrails >= 0 && pathStops && pathStops[selectedTrails]) {
        //Get the selected Trail
        const selectedPathStops = pathStops[selectedTrails] || [];

        // Validate that selectedPathStops is an array before mapping
        if (Array.isArray(selectedPathStops)) {
          let selectedStopIds = selectedPathStops.map((stop) =>
            Number(stop.stopid)
          );
          //Dispatch to render to the map.
          dispatch(setPathStops(selectedPathStops));
          dispatch(setStopState(selectedStopIds, StopState.Selected));
        }
      }
    }, [selectedTrails, pathStops, dispatch]);

    // Handle deep-linked trail navigation
    useEffect(() => {
      if (
        currentTrailIndex !== null && 
        currentTrailIndex !== selectedTrails &&
        suggestedTrails &&
        Array.isArray(suggestedTrails) &&
        suggestedTrails.length > currentTrailIndex &&
        currentTrailIndex >= 0
      ) {
        const trailData = suggestedTrails[currentTrailIndex];
        if (trailData) {
          setSelectedTrails(currentTrailIndex);
          setTrails(trailData);
        }
      }
    }, [currentTrailIndex, selectedTrails, suggestedTrails, setSelectedTrails, setTrails]);

    const trailButtonClick = useCallback(
      (suggestion: any, identifier: string, idx: number) => {
        // Validate suggestion object before proceeding
        if (!suggestion) {
          return;
        }
        
        //Store the trail being selected.
        setTrails(suggestion);

        // Dispatch the trails we want to display
        if (setSelectedTrails) setSelectedTrails(idx);
        
        // Dispatch navigateToTrail to update currentTrailId in state
        // This will trigger URL sync
        const trailId = String(suggestion.trailid || suggestion.id || idx);
        dispatch(navigateToTrail(trailId, idx));

        //if mobile, we want to keep the searchOptions visisible
        if (isMobile) {
          dispatch(setShowSearchOptions(true));
        }
      },
      [dispatch, setTrails, setSelectedTrails]
    );

    useEffect(() => {
      const trailsContainer = document.getElementById("searchOptionsTitle")
      if (view === "trails" && trailsContainer) {
        trailsContainer.focus();
      }
    }, [view])

    return (
      <TrailsList
        suggestions={suggestedTrails}
        selectedTrail={selectedTrail}
        trailButtonClick={trailButtonClick}
        showTrails={showTrails}
      />
    );
  },
  areEqualShallow
);

export default TrailsListContainer;
